import React from "react";
import { useNavigate } from "react-router-dom";

import { useAuth0 } from "@auth0/auth0-react";

import { useTranslation } from "react-i18next";

import {
  Users,
  Landmark,
  Euro,
  ClipboardCheck,
  History,
  ChevronRight,
} from "lucide-react";

import { getAdminDashboard } from "./adminDashboardApi";

import "./AdminDashboard.css";

/* =========================================================
   ANIMATED NUMBER
   ========================================================= */

function useAnimatedNumber(targetValue, duration = 720) {
  const [value, setValue] = React.useState(0);

  React.useEffect(() => {
    const target = Number(targetValue) || 0;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return undefined;
    }

    let animationFrame;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;

      const progress = Math.min(elapsed / duration, 1);

      /*
       * Ease-out cubic:
       * fast initially, smooth finish.
       */
      const eased = 1 - Math.pow(1 - progress, 3);

      setValue(target * eased);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [targetValue, duration]);

  return value;
}

/* =========================================================
   COUNTER
   ========================================================= */

function AnimatedCounter({ value, type = "number", locale = "en-GB" }) {
  const animated = useAnimatedNumber(value);

  if (type === "currency") {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(animated);
  }

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Math.round(animated));
}

/* =========================================================
   SUMMARY CARD
   ========================================================= */

function SummaryCard({ title, subtitle, value, type, locale, icon: Icon }) {
  return (
    <article className="admin-dashboard-card">
      <div className="admin-dashboard-card__top">
        <div>
          <h3>{title}</h3>

          <p>{subtitle}</p>
        </div>

        <span className="admin-dashboard-card__icon">
          <Icon size={18} strokeWidth={2} />
        </span>
      </div>

      <strong className="admin-dashboard-card__value">
        <AnimatedCounter value={value} type={type} locale={locale} />
      </strong>
    </article>
  );
}

/* =========================================================
   ACTIVITY HELPERS
   ========================================================= */

function humanizeActivityValue(value) {
  if (!value) {
    return "";
  }

  return String(value)
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatActivityDate(value, locale) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/* =========================================================
   RECENT ACTIVITY ITEM
   ========================================================= */

function RecentActivityItem({ event, t, locale }) {
  const actor = event?.actor || {};
  const target = event?.target || {};

  const actorName = actor.name || actor.email || t("activity.system", "System");

  const action = event?.action || "";

  const actionLabel = t(
    `activity.actions.${action}`,
    humanizeActivityValue(action),
  );

  const areaLabel = t(
    `activity.areas.${event?.area}`,
    humanizeActivityValue(event?.area),
  );

  const targetLabel =
    target.label ||
    humanizeActivityValue(target.type) ||
    t("activity.unknownTarget", "Platform item");

  const actionClass = String(action).toLowerCase().replaceAll("_", "-");

  return (
    <div className="admin-dashboard-activity-item">
      <span className="admin-dashboard-activity-item__icon">
        <History size={17} strokeWidth={2} />
      </span>

      <div className="admin-dashboard-activity-item__content">
        <div className="admin-dashboard-activity-item__main">
          <strong>{actorName}</strong>

          <span
            className={`admin-dashboard-activity-item__action admin-dashboard-activity-item__action--${actionClass}`}
          >
            {actionLabel}
          </span>

          <span className="admin-dashboard-activity-item__target">
            {targetLabel}
          </span>
        </div>

        <div className="admin-dashboard-activity-item__meta">
          <span>{areaLabel}</span>

          {actor.organisation?.name ? (
            <>
              <span className="admin-dashboard-activity-item__separator">
                •
              </span>

              <span>{actor.organisation.name}</span>
            </>
          ) : null}
        </div>
      </div>

      <time className="admin-dashboard-activity-item__time">
        {formatActivityDate(event?.created_at, locale)}
      </time>
    </div>
  );
}

/* =========================================================
   DASHBOARD
   ========================================================= */

export default function AdminDashboard() {
  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { t, i18n } = useTranslation("admin-dashboard");

  const [data, setData] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [error, setError] = React.useState("");

  const locale =
    i18n.language === "nl"
      ? "nl-BE"
      : i18n.language === "es"
        ? "es-ES"
        : i18n.language === "ca"
          ? "ca-ES"
          : "en-GB";

  const loadDashboard = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const accessToken = await getAccessTokenSilently();

      const response = await getAdminDashboard({
        accessToken,
      });

      setData(response);
    } catch (loadError) {
      console.error("Could not load admin dashboard:", loadError);

      setError(
        loadError.message || t("errors.load", "Could not load dashboard data."),
      );
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, t]);

  React.useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const summary = data?.summary || {};

  const categories = data?.user_categories || {};

  const requests = data?.pending_requests || [];

  const recentActivity = data?.recent_activity || [];

  const cards = [
    {
      key: "users",
      title: t("summary.totalUsers.title"),
      subtitle: t("summary.totalUsers.subtitle"),
      value: summary.total_users || 0,
      icon: Users,
    },
    {
      key: "loanCases",
      title: t("summary.loanCases.title"),
      subtitle: t("summary.loanCases.subtitle"),
      value: summary.total_loan_cases || 0,
      icon: Landmark,
    },
    {
      key: "loanVolume",
      title: t("summary.loanVolume.title"),
      subtitle: t("summary.loanVolume.subtitle"),
      value: summary.total_loan_volume || 0,
      type: "currency",
      icon: Euro,
    },
    {
      key: "requests",
      title: t("summary.pending.title"),
      subtitle: t("summary.pending.subtitle"),
      value: summary.pending_requests || 0,
      icon: ClipboardCheck,
    },
  ];

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard__loading">
          <div className="admin-dashboard__loading-bar" />
          <div className="admin-dashboard__loading-grid">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="admin-dashboard__skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard__error">
          <strong>{t("errors.title", "Dashboard unavailable")}</strong>

          <p>{error}</p>

          <button type="button" onClick={loadDashboard}>
            {t("actions.retry", "Try again")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* ===================================================
          HEADER
          =================================================== */}

      <header className="admin-dashboard__header">
        <div className="admin-dashboard__header-copy">
          <h1>{t("header.title")}</h1>

          <p>{t("header.subtitle")}</p>
        </div>

        <span className="admin-dashboard__governance-badge">
          {t("header.governance")}
        </span>
      </header>

      {/* ===================================================
          ROLE INFORMATION
          =================================================== */}

      <section className="admin-dashboard__role-note">
        <strong>{t("role.title")}</strong>

        <p>{t("role.description")}</p>
      </section>

      {/* ===================================================
          SUMMARY
          =================================================== */}

      <section className="admin-dashboard__summary-grid">
        {cards.map((card) => (
          <SummaryCard key={card.key} {...card} locale={locale} />
        ))}
      </section>

      {/* ===================================================
          LOWER GRID
          =================================================== */}

      <section className="admin-dashboard__lower-grid">
        {/* PENDING REQUESTS */}

        <article className="admin-dashboard-panel">
          <div className="admin-dashboard-panel__header">
            <div>
              <h2>{t("requests.title")}</h2>

              <p>{t("requests.subtitle")}</p>
            </div>

            <span className="admin-dashboard-panel__count admin-dashboard-panel__count--warning">
              {summary.pending_requests || 0} {t("requests.open")}
            </span>
          </div>

          <div className="admin-dashboard-requests">
            {requests.length === 0 ? (
              <div className="admin-dashboard-requests__empty">
                {t("requests.empty")}
              </div>
            ) : (
              requests.map((request) => (
                <button
                  type="button"
                  key={`${request.request_type}-${request.id}`}
                  className="admin-dashboard-request"
                  onClick={() =>
                    navigate("/admin/requests", {
                      state: {
                        requestId: request.id,
                        requestType: request.request_type,
                      },
                    })
                  }
                >
                  <span className="admin-dashboard-request__copy">
                    <strong>{request.title}</strong>

                    <span>
                      {t(
                        `requestTypes.${request.request_type}`,
                        request.request_type,
                      )}

                      {request.region ? ` • ${request.region}` : ""}
                    </span>
                  </span>

                  <span className="admin-dashboard-request__review">
                    {t("actions.review")}
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="admin-dashboard-panel__actions">
            <button
              type="button"
              className="admin-dashboard-btn admin-dashboard-btn--primary"
              onClick={() => navigate("/admin/requests")}
            >
              {t("actions.openRequests")}
            </button>

            <button
              type="button"
              className="admin-dashboard-btn admin-dashboard-btn--secondary"
              onClick={() => navigate("/admin/requests?type=controlled-edit")}
            >
              {t("actions.viewEditQueue")}
            </button>
          </div>
        </article>

        {/* USER CATEGORIES */}

        <article className="admin-dashboard-panel">
          <div className="admin-dashboard-panel__header">
            <div>
              <h2>{t("users.title")}</h2>

              <p>{t("users.subtitle")}</p>
            </div>

            <span className="admin-dashboard-panel__count">
              {summary.total_users || 0} {t("users.users")}
            </span>
          </div>

          <div className="admin-dashboard-users-grid">
            <div className="admin-dashboard-user-category">
              <span>{t("users.homebuyers")}</span>

              <strong>
                <AnimatedCounter
                  value={categories.homeowners || 0}
                  locale={locale}
                />
              </strong>
            </div>

            <div className="admin-dashboard-user-category">
              <span>{t("users.lenders")}</span>

              <strong>
                <AnimatedCounter
                  value={categories.lenders || 0}
                  locale={locale}
                />
              </strong>
            </div>

            <div className="admin-dashboard-user-category">
              <span>{t("users.oss")}</span>

              <strong>
                <AnimatedCounter value={categories.oss || 0} locale={locale} />
              </strong>
            </div>

            <div className="admin-dashboard-user-category">
              <span>{t("users.admins")}</span>

              <strong>
                <AnimatedCounter
                  value={categories.admins || 0}
                  locale={locale}
                />
              </strong>
            </div>
          </div>

          <div className="admin-dashboard-panel__actions">
            <button
              type="button"
              className="admin-dashboard-btn admin-dashboard-btn--primary"
              onClick={() => navigate("/admin/users")}
            >
              {t("actions.openUsers")}
            </button>

            <button
              type="button"
              className="admin-dashboard-btn admin-dashboard-btn--secondary"
              onClick={() => navigate("/admin/users?view=access")}
            >
              {t("actions.manageAccess")}
            </button>
          </div>
        </article>
      </section>
      {/* ===================================================
          RECENT ACTIVITY
          =================================================== */}

      <section className="admin-dashboard-panel admin-dashboard-activity-panel">
        <div className="admin-dashboard-panel__header admin-dashboard-activity-panel__header">
          <div>
            <h2>{t("activity.title", "Recent activity")}</h2>

            <p>
              {t(
                "activity.subtitle",
                "Latest administrative and platform changes.",
              )}
            </p>
          </div>

          <button
            type="button"
            className="admin-dashboard-activity__view-all"
            onClick={() => navigate("/admin/activity")}
          >
            <span>{t("activity.viewAll", "View all activity")}</span>

            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </div>

        {recentActivity.length === 0 ? (
          <div className="admin-dashboard-activity__empty">
            <span className="admin-dashboard-activity__empty-icon">
              <History size={19} strokeWidth={2} />
            </span>

            <div>
              <strong>{t("activity.emptyTitle", "No recent activity")}</strong>

              <p>
                {t(
                  "activity.emptyDescription",
                  "Platform activity will appear here as changes are recorded.",
                )}
              </p>
            </div>
          </div>
        ) : (
          <div className="admin-dashboard-activity-list">
            {recentActivity.map((event) => (
              <RecentActivityItem
                key={event.id}
                event={event}
                t={t}
                locale={locale}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
